// nuolino-admin/src/features/patients/patients-api.ts
// Service tipado de pacientes. Camada fina sobre http.ts.

import { http } from '@/lib/http';
import type { LinkStatus } from '@/components/ui';

export interface Patient {
  avatarUrl?: string;
  birthDate?: string;
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: LinkStatus;
  goal?: string;
  lastSeenAt?: string | null;
  /** true se o nutricionista logado cadastrou este paciente manualmente.
   *  Habilita a edição de dados pessoais no admin (LGPD). */
  createdByNutritionist?: boolean;
}

export interface PatientsQuery {
  page: number;
  pageSize: number;
  search?: string;
  status?: LinkStatus | 'all';
  sortColumn?: 'name' | 'lastSeenAt';
  sortDirection?: 'asc' | 'desc';
}

export interface PatientsResponse {
  rows: Patient[];
  total: number;
}

export async function listPatients(query: PatientsQuery, signal?: AbortSignal): Promise<PatientsResponse> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  if (query.search) params.set('search', query.search);
  if (query.status && query.status !== 'all') params.set('status', query.status);
  if (query.sortColumn) params.set('sortBy', query.sortColumn);
  if (query.sortDirection) params.set('sortDir', query.sortDirection);
  return http.get<PatientsResponse>(`/api/v1/patients?${params.toString()}`, { signal });
}

export interface CreatePatientInput {
  // Identificacao
  name: string;
  email: string;
  phone?: string;
  birthDate?: string;
  gender?: string;
  age?: number;
  // Medidas
  weightKg?: string;
  heightCm?: string;
  goal?: string;
  // Anamnese
  objectives?: string[];
  allergies?: string[];
  foodRestrictions?: string[];
  activityLevel?: string;
  medications?: string[];
  medicalHistory?: string[];
  foodPreferences?: string[];
  foodAversions?: string[];
  notes?: string;
}

export interface CreatePatientResponse {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: string;
  anamnesisId?: string;
}

export async function createPatient(input: CreatePatientInput): Promise<CreatePatientResponse> {
  return http.post<CreatePatientResponse>('/api/v1/patients', input);
}

// ── Privacidade do paciente (LGPD) ──────────────────────────────────────
// Indica se o nutricionista logado pode ver/editar email/telefone do paciente.
// canEditPersonalData = true quando o nutricionista cadastrou o paciente
// (já tem os dados do formulário de intake) OU o paciente optou por
// compartilhar (shareDataWithNutritionist=true nas settings do app).
export interface PatientPrivacy {
  patientId: string;
  createdByNutritionist: boolean;
  shareDataWithNutritionist: boolean;
  shareClinicalDataWithNutritionist: boolean;
  sharePhotosWithNutritionist: boolean;
  canEditPersonalData: boolean;
  canEditClinicalData: boolean;
}

export async function getPatientPrivacy(patientId: string, signal?: AbortSignal): Promise<PatientPrivacy> {
  return http.get<PatientPrivacy>(`/api/v1/patients/${patientId}/privacy`, { signal });
}

export interface UpdatePersonalDataInput {
  name?: string;
  email?: string;
  phone?: string;
}

export async function updatePatientPersonalData(
  patientId: string,
  input: UpdatePersonalDataInput,
): Promise<Patient> {
  return http.put<Patient>(`/api/v1/patients/${patientId}/personal-data`, input);
}
